import { DateTime } from 'luxon';
import {
  Order,
  OrderDocumentType,
  OrderProduct,
  OrderProductOption,
  OrderReturnStatus,
  OrderStatus,
  OrderType,
} from '../../../clients/quipli-service/order/order';
import {
  CalculateRentalPriceRequest,
  CustomOrderPriceRequest,
  isErrorCalculatePriceResult,
  RentalProductPrice,
} from '../../../clients/quipli-service/pricing/pricing';
import classNames from 'classnames';
import { Typography } from '../../../components/typography/typography';
import { convertToMoney, displayMoneyFormat, validateFalsy } from '../../../util/util';
import { EditableField, FieldCounter } from '../../../components/field/field';
import { OrderProductStatusBadge } from './order-product-status-badge';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { DateTimeCell } from '../order-table-cells';
import { DateTimePicker } from '../../../components/date-picker/date-time-picker';
import { toast } from '../../../util/toasts';
import { Location } from '../../../clients/quipli-service/location/location';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import {
  isOrderCustomInput,
  isSaleInput,
  OrderInputItem,
  OrderInputRental,
} from '../../../clients/quipli-service/order/order-calculation';
import {
  CustomProductLineItem,
  ProductFeeLineItem,
  ProductLineItem,
} from '../../../clients/quipli-service/financials/financials';
import { Spinner } from '../../../components/spinner/spinner';
import { formatShippingType } from '../../../clients/quipli-service/delivery/delivery';
import usePrevious from '../../../hooks/use-previous';
import { Currency, Dinero } from 'dinero.js';
import { ProductImageV2 } from '../../../components/image/product/product-image-v2';
import { ProductTrackingType, ProductType } from '../../../clients/quipli-service/product/product';
import { AssignUnitSelect } from '../assign-unit-select/assign-unit-select';
import { AssignedUnits } from '../assigned-units/assigned-units';
import { useTaxApiV1 } from '../../../clients/quipli-service/tax/tax-api-v1';
import { ItemType } from '../../../clients/quipli-service/accounting/accounting';

function extractProps(
  orderProduct?: OrderProduct,
  inputItem?: OrderInputItem,
  financialItem?: ProductLineItem | CustomProductLineItem,
) {
  const isRental = (inputItem?.type || orderProduct?.type) === OrderType.Rental;
  const isNewProduct = !!inputItem && !orderProduct;

  // Rental product properties
  let units = isRental ? (inputItem as OrderInputRental | undefined)?.assignedUnits : [];

  if (!units?.length && orderProduct?.assignedUnits) {
    units = orderProduct.assignedUnits.map((unit) => unit.id);
  }

  const startDate = (
    isRental
      ? (inputItem as OrderInputRental | undefined)?.start || orderProduct?.rentalStartDate
      : orderProduct?.rentalStartDate
  )!;
  const endDate = isRental ? (inputItem as OrderInputRental | undefined)?.end : undefined;
  const originalCustomOrderRentalRates = orderProduct?.customOrderRentalRates;
  const customOrderRentalRates =
    (inputItem as OrderInputRental | undefined)?.customOrderRentalRates || orderProduct?.customOrderRentalRates;

  // const delivery =
  //   inputItem?.deliveryType ||
  //   inputItem?.deliveryOptions.find((d) => d.isSelected)?.type ||
  //   (orderProduct?.shippingTypeId ? toShippingType(orderProduct.shippingTypeId) : undefined);
  const quantity = (inputItem?.quantity || orderProduct?.quantity)!;

  // Discount
  const originalDiscount = orderProduct?.discountPercentage || 0;
  const discount =
    (inputItem?.manualDiscount || inputItem?.manualDiscount === 0 ? inputItem.manualDiscount : originalDiscount) || 0;

  // Price
  const originalUnitPrice = isNewProduct
    ? financialItem?.hasCustomAmount
      ? -1
      : financialItem?.unitPrice.toUnit()
    : orderProduct?.customerBuyingPrice;
  const unitPrice = (financialItem?.unitPrice.toUnit() ||
    (!!inputItem?.customPrice || inputItem?.customPrice === 0 ? inputItem.customPrice / 100 : originalUnitPrice))!;
  const total = unitPrice * quantity * (discount ? 1 - discount / 100 : 1);

  return {
    isRental,
    isNewProduct,
    units,
    startDate,
    endDate,
    originalCustomOrderRentalRates,
    customOrderRentalRates,
    // delivery,
    quantity,
    originalDiscount,
    discount,
    originalUnitPrice,
    unitPrice,
    total,
  };
}

export const OrderContractProduct = ({
  order,
  product,
  location,
  setEditsValid,
  isEditing,
  isShowingPrices,
  isCustomOrderRentalRatesEnabled,
  isTaxInclusive,
  onlyShowStoreHoursOnDateTimePicker,
  rentalPrices,
  inputItem,
  financialItem,
  financialFees,
  onChange,
  onDelete,
  onChangeFee,
  onResetFee,
  refresh,
  saleSerialTrackingEnabled,
  deliveryDescription,
  taxCodeId,
}: {
  order: Order;
  product?: OrderProduct;
  location: Location;
  setEditsValid: (valid: boolean) => void;
  isEditing: boolean;
  isShowingPrices: boolean;
  isCustomOrderRentalRatesEnabled: boolean;
  isTaxInclusive: boolean;
  onlyShowStoreHoursOnDateTimePicker: boolean;
  rentalPrices?: RentalProductPrice[];
  inputItem?: OrderInputItem;
  financialItem?: ProductLineItem | CustomProductLineItem;
  financialFees: ProductFeeLineItem[];
  onChange: (orderInputItem: OrderInputItem) => void;
  onDelete: (orderInputId: string) => void;
  onChangeFee: (inputFee: ProductFeeLineItem, newAmount: number) => void;
  onResetFee: (inputFee: ProductFeeLineItem) => void;
  refresh: () => Promise<void>;
  saleSerialTrackingEnabled: boolean;
  deliveryDescription?: { productId: number; text: string };
  taxCodeId?: string;
}) => {
  const { assignUnits, calculatePriceAsAdmin, getProduct, getAvailableCount } = useAPI();
  const { getItemTax } = useTaxApiV1();
  const {
    isRental,
    isNewProduct,
    units,
    startDate,
    endDate,
    originalCustomOrderRentalRates,
    customOrderRentalRates,
    // delivery,
    quantity,
    originalDiscount,
    discount,
    originalUnitPrice,
    unitPrice,
    total,
  } = extractProps(product, inputItem, financialItem);

  const [isLoading, setIsLoading] = useState(false);
  const [taxedUnitPrice, setTaxedUnitPrice] = useState<number | undefined>(unitPrice);
  const [totalTaxPercentage, setTotalTaxPercentage] = useState<number | undefined>(undefined);
  const [trackingType, setTrackingType] = useState<ProductTrackingType | undefined>(product?.product.trackingType);
  const [qtyAvailable, setQtyAvailable] = useState<number | undefined>(
    isRental
      ? undefined
      : product?.product.stock?.find((s) => s.locationId === location.id)?.saleStock || product?.product.saleStock,
  );
  const [stock, setStock] = useState<number | undefined>(
    product?.product.stock?.find((s) => s.locationId === location.id)?.rentalStock || product?.product.rentalStock,
  );
  const [showQtyWarning, setShowQtyWarning] = useState<boolean>(false);
  const [showOverbookWarning, setShowOverbookWarning] = useState<boolean>(false);

  // Start & End Date Validation
  const [startDateInput, setStartDateInput] = useState<DateTime>(startDate);
  const [endDateInput, setEndDateInput] = useState<DateTime | undefined>(endDate);
  const [isEndDateInputValid, setIsEndDateInputValid] = useState<boolean>(true);
  const prevStart = usePrevious(startDateInput);
  const prevEnd = usePrevious(endDateInput);

  const TrackingTypeAvailableQuantity = () => {
    if (
      (trackingType === ProductTrackingType.SerialUnits &&
        (saleSerialTrackingEnabled || inputItem?.type === OrderType.Rental)) ||
      trackingType === ProductTrackingType.NonInventory
    ) {
      return null;
    }

    if (!isEditing) {
      return <>{`Qty: ${product?.quantity}`}</>;
    }

    if (isRental && (qtyAvailable === undefined || stock === undefined)) {
      return <Spinner />;
    }

    return (
      <>{`${qtyAvailable && Number(qtyAvailable).toFixed(2)} ${
        stock && isRental ? `of ${Number(stock).toFixed(2)}` : ''
      } units available`}</>
    );
  };

  const checkAvailableQuantity = (
    newQty: number,
    schedule?: { start: DateTime; end?: DateTime },
    trackingTypeOverride?: ProductTrackingType,
  ) => {
    const currentTrackingType = trackingTypeOverride ?? trackingType;

    if (
      currentTrackingType === undefined ||
      currentTrackingType === ProductTrackingType.NonInventory ||
      (inputItem && isOrderCustomInput(inputItem))
    ) {
      return;
    }

    const from = schedule?.start || startDate;
    const to = schedule?.end || endDate || from.plus({ days: 28 });
    const name = product?.product?.name || financialItem?.description;
    getAvailableCount(inputItem!.productId, location.id, inputItem?.type, from, to, [order.id]).then((av) => {
      const isTrackedBySerial = currentTrackingType === ProductTrackingType.SerialUnits;

      // Set available quantity for tracked by quantity (this number is visible in the UI)
      !isTrackedBySerial && setQtyAvailable(av);

      // Display warning if the available quantity is less than the new quantity (the warning depends on the tracking type)
      if (av < newQty) {
        // Show the correct warning based on the tracking type
        if (isTrackedBySerial) {
          setShowOverbookWarning(true);
        } else {
          setShowQtyWarning(true);
          toast(`Warning: You are exceeding the available quantity${name ? ` of ${name}` : ''}.`, 'warning');
        }
      }

      // Hide the warning if the available quantity is greater than or equal to the new quantity
      if (av >= newQty) {
        setShowQtyWarning(false);
        setShowOverbookWarning(false);
      }
    });
  };

  useEffect(() => {
    if (taxCodeId && unitPrice && (inputItem?.productId.toString() || product?.id.toString())) {
      const taxable = product?.isTaxFree !== true ?? financialItem?.taxable ?? true;
      getItemTax({
        id: taxCodeId,
        itemPrice: convertToMoney(unitPrice, location.currency as Currency),
        itemId: (isNewProduct ? inputItem?.productId.toString() : product?.id.toString())!,
        transactionType: isRental ? OrderType.Rental : OrderType.Sale,
        taxable,
        itemType: ItemType.Product,
      }).then((tax) => {
        if (tax) {
          setTaxedUnitPrice(unitPrice * (1 + tax.totalPercentage / 100));
          setTotalTaxPercentage(tax.totalPercentage);
        }
      });
    }
  }, [unitPrice, taxCodeId, isNewProduct, inputItem?.productId, product?.id, isRental]);

  const changeStart = useCallback(
    (date: DateTime | undefined) => {
      if (location.id && date) {
        if (!inputItem || isSaleInput(inputItem)) {
          return;
        }
        updateRentalPriceDates(
          {
            productId: inputItem!.productId,
            orderProductId: !isNaN(Number(inputItem.id)) ? parseInt(inputItem.id) : undefined,
            type: OrderType.Rental,
            start: date,
            end: endDateInput,
            locationId: location.id,
            orderId: order.id,
            taxCodeId: taxCodeId,
            customOrderRentalRates: customOrderRentalRates?.filter((r) => {
              return !!(r as RentalProductPrice).originalPrice?.id || !!r.action;
            }),
          },
          'start',
        );
      }
    },
    [inputItem, location, order, customOrderRentalRates, endDateInput],
  );

  const changeEnd = useCallback(
    (date: DateTime | undefined, startDate: DateTime) => {
      if (!inputItem || isSaleInput(inputItem)) {
        return;
      }
      if (location.id && startDate) {
        updateRentalPriceDates(
          {
            productId: inputItem!.productId,
            orderProductId: !isNaN(Number(inputItem.id)) ? parseInt(inputItem.id) : undefined,
            type: OrderType.Rental,
            start: startDate,
            end: date,
            locationId: location.id,
            orderId: order.id,
            taxCodeId: taxCodeId,
            customOrderRentalRates: customOrderRentalRates?.filter((r) => {
              return !!(r as RentalProductPrice).originalPrice?.id || !!r.action;
            }),
          },
          'end',
        );
      }
    },
    [inputItem, location, order, customOrderRentalRates],
  );

  useEffect(() => {
    setEditsValid(isEndDateInputValid);
  }, [isEndDateInputValid]);

  const handleStartDateChange = (date: DateTime) => {
    setStartDateInput(date);
    if (endDateInput && date && date > endDateInput) {
      setIsEndDateInputValid(false);
      toast('End date cannot be before start date, please adjust.', 'warning');
    } else {
      setIsEndDateInputValid(true);
      changeStart(date);
    }
  };

  const handleEndDateChange = useCallback(
    (date: DateTime | undefined) => {
      setEndDateInput(date);
      if (startDateInput && date && date < startDateInput) {
        setIsEndDateInputValid(false);
        toast('End date cannot be before start date, please adjust.', 'warning');
      } else {
        setIsEndDateInputValid(true);
        changeEnd(date, startDateInput);
      }
    },
    [startDateInput],
  );

  // Get the new rental price when dates are changed
  const updateRentalPriceDates = useCallback(
    debounce(async (request: Omit<CalculateRentalPriceRequest, 'end'> & { end?: DateTime }, type: 'start' | 'end') => {
      const end =
        order.isCycleBilled || !request.end
          ? request.start.plus(order.cycleBillingTerms?.cycleLength || { days: 28 })
          : request.end;
      if (!inputItem) {
        return;
      }

      // eslint-disable-next-line prefer-const
      let { end: oldEnd, ...updatedInputItem } = { ...inputItem } as OrderInputRental;

      if (!isNewProduct) {
        let customPrice: Dinero;

        if ((prevStart && +prevStart !== +request.start) || (prevEnd && +prevEnd !== +end)) {
          try {
            const res = await calculatePriceAsAdmin({ ...request, end });

            if (isErrorCalculatePriceResult(res)) {
              toast(res.error, 'error');
              return;
            }

            customPrice = convertToMoney(res.totalPrice / 100, location.currency as Currency);
          } catch (e: any) {
            toast(e.message, 'error');
            return;
          }
        } else {
          customPrice = convertToMoney(unitPrice, location.currency as Currency);
        }

        updatedInputItem = {
          ...updatedInputItem,
          start: request.start,
          customPrice: customPrice.getAmount(),
        };
      }

      const changePayload: OrderInputRental = {
        ...updatedInputItem,
        end: type === 'start' ? oldEnd : request.end,
      };

      // Check availability based on new dates and quantity
      checkAvailableQuantity(updatedInputItem.quantity, { start: changePayload.start, end: changePayload.end });

      onChange(changePayload);
    }, 200),
    [onChange, inputItem, unitPrice, isNewProduct, prevStart, prevEnd, location, isTaxInclusive],
  );

  // On cancel, reset any intermediate state
  useEffect(() => {
    if (!isEditing) {
      setStartDateInput(product?.rentalStartDate || startDate);
      setEndDateInput(product?.rentalEndDate);
      setShowOverbookWarning(false);
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isRental || !!product) return;

    if (isNewProduct && isRental && inputItem && !isOrderCustomInput(inputItem)) {
      setIsLoading(true);
      getProduct(inputItem.productId).then(async (product) => {
        setTrackingType(product.trackingType);
        setStock(product.stock?.find((s) => s.locationId === location.id)?.rentalStock || product.rentalStock);
        checkAvailableQuantity(inputItem.quantity, undefined, product.trackingType);
        setIsLoading(false);
      });
    }
  }, []);

  // sync the date states whenever props change
  useEffect(() => {
    if (startDate && (!startDateInput || +startDateInput !== +startDate)) {
      setStartDateInput(startDate);
    }

    const propsEndDate = isRental ? endDate : undefined;
    if ((!endDateInput && propsEndDate) || (propsEndDate && endDateInput && +endDateInput !== +propsEndDate)) {
      setEndDateInput(propsEndDate);
    }
  }, [startDate, endDate, product, inputItem]);

  const startDaysHours =
    onlyShowStoreHoursOnDateTimePicker && startDateInput
      ? location.hoursOfOperation.find((ho) => ho.numericDayOfWeek === startDateInput.weekday)?.rentalStart
      : undefined;
  const endDaysHours =
    onlyShowStoreHoursOnDateTimePicker && endDateInput
      ? location.hoursOfOperation.find((ho) => ho.numericDayOfWeek === endDateInput.weekday)?.rentalEnd
      : undefined;

  return inputItem ? (
    <div className="overflow-x-auto order-contract-wrap min-w-[0] my-[8px] border border-solid border-grey-200 rounded-lg flex flex-wrap">
      <div className="flex-[0_1_100%] flex">
        {/* Name, fees, options, status */}
        <div className={classNames('flex-[0_1_17%] flex flex-col')}>
          <div className="truncate min-h-[25px] py[12px] px-[8px] border-t-0 border-x-0 border-b border-solid border-b-[var(--grey-200)]">
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
              Product Name
            </Typography>
          </div>
          <div className="min-h-[70px] min-w-[250px] py[12px] px-[8px] flex items-center">
            <ProductImageV2
              productId={
                isOrderCustomInput(inputItem) || product?.product.type === ProductType.Custom
                  ? 'placeholder'
                  : inputItem.productId
              }
              profile="MINI"
              className="min-h-[48px] min-w-[48px] max-h-[48px] max-w-[48px] aspect-square  rounded-[6.85px] border-[0.87px] border-solid border-grey-200 overflow-hidden "
            />
            <div className="ml-[8px] flex flex-col">
              {isNewProduct && !financialItem ? (
                <Spinner />
              ) : (
                <Typography type="text-sm" weight="regular" className="m-[2px] text-[var(--quipli-black-800)]">
                  {product?.product.type === ProductType.Custom
                    ? `${product.name}${product.description ? `: ${product.description}` : ''}`
                    : product?.product?.sku
                    ? `${product?.product?.name} (${product?.product?.sku})`
                    : product?.product?.name || financialItem?.description}
                </Typography>
              )}

              {product?.options.map((option: OrderProductOption) => (
                <Typography key={option.id} type="text-xs" weight="medium" className="text-[var(--quipli-black-800)]">
                  {option.name}: {option.value}
                </Typography>
              ))}

              {!isRental && product && product.services.length > 0 && (
                <div className="flex flex-col gap-1">
                  <Typography type="text-xs" weight="semi-bold" className="text-quipli-black-500">
                    Cargo Requirements:
                  </Typography>

                  <ul>
                    {product.services.map((service) => {
                      return (
                        <li key={`product-${product.id}-service-${service.id}`}>
                          <div className="flex gap-1 ml-2">
                            <Typography type="text-xs" className="text-quipli-black-700">
                              - {service.name}:
                            </Typography>
                            <Typography type="text-xs" weight={'semi-bold'} className="text-quipli-black-700">
                              {service.value}
                            </Typography>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {financialFees.map((fee) => {
                let feeTitle = '';
                const taxableTitle = fee.taxable ? ' - (Taxable)' : '';

                if (order.isCycleBilled) {
                  if (fee.recurring) {
                    feeTitle = fee.description + taxableTitle + ' - EA: ';
                  } else {
                    feeTitle = '*' + fee.description + taxableTitle + ': ';
                  }
                } else {
                  feeTitle = fee.description + taxableTitle + ': ';
                }

                return isEditing && !isTaxInclusive ? (
                  <Typography
                    key={`fee-${fee.productLineItemId}-${fee.productFeeId}`}
                    type="text-xs"
                    weight="medium"
                    className="text-[var(--quipli-black-800)] flex flex-wrap gap-1"
                  >
                    {feeTitle}
                    <EditableField
                      type="number"
                      label={feeTitle}
                      value={fee.amount.toUnit()}
                      formattedValue={fee.amount.toFormat()}
                      canReset={fee.hasCustomAmount}
                      resetPosition="right"
                      onReset={() => onResetFee(fee)}
                      onChange={(value) => onChangeFee(fee, value)}
                    />
                  </Typography>
                ) : (
                  <Typography
                    key={`fee-edit-${fee.productLineItemId}-${fee.productFeeId}`}
                    type="text-xs"
                    weight="medium"
                    className="text-[var(--quipli-black-800)]"
                  >
                    {`${feeTitle} ${fee.amount.toFormat()}`}
                  </Typography>
                );
              })}

              {product && order.documentType == 'Order' && <OrderProductStatusBadge orderProduct={product} />}

              {product?.equipmentOwner && (
                <Typography type="text-xs" weight="regular" className="m-[2px] text-[var(--quipli-grey-400)]">
                  Equipment Owner: {product?.equipmentOwner}
                </Typography>
              )}
            </div>
          </div>
        </div>

        {/* Units */}
        <div className={classNames('flex-[0_1_17%] flex flex-col')}>
          <div className="truncate min-h-[25px] py[12px] px-[8px] border-t-0 border-x-0 border-b border-solid border-b-[var(--grey-200)]">
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
              Unit
            </Typography>
          </div>
          <div className="min-h-[70px] min-w-[150px] py[12px] px-[8px] flex flex-col items-start justify-center">
            {isLoading ? (
              <Spinner />
            ) : (
              <>
                {trackingType === ProductTrackingType.NonInventory ? (
                  <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
                    N/A
                  </Typography>
                ) : trackingType === ProductTrackingType.Quantity ||
                  (!saleSerialTrackingEnabled && inputItem.type === OrderType.Sale) ? (
                  <div className={classNames('flex flex-col gap-2')}>
                    <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
                      <TrackingTypeAvailableQuantity />
                    </Typography>

                    {showQtyWarning && (
                      <Typography type="text-sm" weight="semi-bold" className="!text-warning-700 py-4">
                        Exceeding available quantity
                      </Typography>
                    )}
                  </div>
                ) : (
                  <>
                    {(inputItem.type === OrderType.Rental && order.returnStatus === OrderReturnStatus.NotStarted) ||
                    (inputItem.type === OrderType.Sale && order.status !== OrderStatus.Approved) ||
                    isNewProduct ? (
                      <AssignUnitSelect
                        locationId={location.id}
                        uniqueId={`${product?.id}`}
                        productId={inputItem.productId}
                        quantity={quantity}
                        startDate={startDate}
                        endDate={endDate}
                        selectedUnitIds={units}
                        disabled={order.documentType !== OrderDocumentType.Order}
                        onChange={(units) => {
                          if (isNewProduct) {
                            onChange({ ...inputItem, assignedUnits: units } as OrderInputRental);
                          } else {
                            assignUnits(order.id, product!.id, units)
                              .then(() => {
                                refresh();
                                toast('Units assigned successfully');
                              })
                              .catch(() => {
                                toast('Error assigning units', 'error');
                              });
                          }
                        }}
                        orderProductType={inputItem.type === OrderType.Sale ? OrderType.Sale : OrderType.Rental}
                      />
                    ) : (
                      <AssignedUnits locationId={location.id} orderId={order.id} orderProductId={product!.id} />
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Rental start / end */}
        <div className="flex-[0_1_16%] flex flex-col">
          <div className="truncate min-h-[25px] py[12px] px-[8px] border-t-0 border-x-0 border-b border-solid border-b-[var(--grey-200)] flex justify-between">
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
              Rental start / end
            </Typography>

            {isRental && isEditing && <Icon icon="Calendar" size="medium" />}
          </div>
          <div className="min-h-[70px] py-[12px] px-[8px] py[12px] flex flex-col justify-center items-center">
            {isRental ? (
              <div className="ml-[2px] min-w-full flex gap-2 items-center">
                <div className="flex-[0_1_50%]">
                  {isEditing ? (
                    <DateTimePicker
                      value={startDateInput}
                      onChange={(date) => {
                        date && handleStartDateChange(date);
                      }}
                      minTime={startDaysHours?.start}
                      maxTime={startDaysHours?.end}
                      timezone={location.timezone}
                      usePortal={true}
                      customDisplay={<DateTimeCell date={startDateInput} isEdit={true} />}
                    />
                  ) : (
                    product?.rentalStartDate && <DateTimeCell date={product?.rentalStartDate} />
                  )}
                </div>
                <div className="flex-[0_1_50%]">
                  {isEditing ? (
                    <DateTimePicker
                      value={endDateInput}
                      onChange={(date) => {
                        handleEndDateChange(date);
                      }}
                      minTime={endDaysHours?.start}
                      maxTime={endDaysHours?.end}
                      timezone={location.timezone}
                      usePortal={true}
                      customDisplay={
                        (endDate && <DateTimeCell date={endDateInput} isEdit={true} />) || <span>Not set</span>
                      }
                      customErrorDisplay={<Icon icon="CircleExclamation" size="small" />}
                      clearable={order.isCycleBilled}
                    />
                  ) : (
                    product?.rentalEndDate && <DateTimeCell date={product?.rentalEndDate} />
                  )}
                </div>
              </div>
            ) : (
              <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
                N/A
              </Typography>
            )}

            {isRental && showOverbookWarning && (
              <Typography type="text-sm" weight="semi-bold" className="!text-error-700 py-4">
                Warning: Another order is already scheduled for these dates.
              </Typography>
            )}
          </div>
        </div>

        {/* Delivery */}
        <div className="flex-[0_1_16%] flex flex-col">
          <div className="truncate min-h-[25px] py[12px] px-[8px] border-t-0 border-x-0 border-b border-solid border-b-[var(--grey-200)]">
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
              Delivery
            </Typography>
          </div>

          <div className="min-h-[70px] py[12px] px-[8px] flex items-center">
            <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
              {formatShippingType(product?.shippingType, product?.type)}
            </Typography>
          </div>
        </div>

        {/* Price */}
        <div className="flex-[0_1_8%] flex shrink-0 flex-col">
          <div className="truncate min-h-[25px] py[12px] px-[8px] border-t-0 border-x-0 border-b border-solid border-b-[var(--grey-200)]">
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
              Unit Price
            </Typography>
          </div>

          <div className="min-h-[70px] py[12px] px-[8px] flex items-center">
            {isEditing && !isTaxInclusive && (!order.isCycleBilled || !isRental) ? (
              <EditableField
                type="number"
                label="Unit Price"
                value={validateFalsy(unitPrice) ? unitPrice : originalUnitPrice}
                formattedValue={displayMoneyFormat(
                  validateFalsy(unitPrice) ? unitPrice : originalUnitPrice,
                  location.currency,
                )}
                canReset={unitPrice !== originalUnitPrice && !isOrderCustomInput(inputItem)}
                resetPosition="right"
                onReset={() => onChange({ ...inputItem, customPrice: undefined })}
                onChange={(value) => onChange({ ...inputItem, customPrice: Math.round(value * 100) })}
              />
            ) : (
              <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
                {displayMoneyFormat(isTaxInclusive ? taxedUnitPrice : unitPrice, location.currency)}
              </Typography>
            )}
          </div>
        </div>

        {/* Discount */}
        <div className="flex-[0_1_8%] flex flex-col">
          <div className="truncate min-h-[25px] py[12px] px-[8px] border-t-0 border-x-0 border-b border-solid border-b-[var(--grey-200)]">
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
              Dis. %
            </Typography>
          </div>
          <div className="min-h-[70px] py[12px] px-[8px] flex items-center">
            {isOrderCustomInput(inputItem) ? (
              <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
                N/A
              </Typography>
            ) : isEditing ? (
              <EditableField
                type="number"
                label="Discount"
                value={validateFalsy(discount) ? discount : originalDiscount}
                formattedValue={`% ${validateFalsy(discount) ? discount : originalDiscount}`}
                canReset={discount !== originalDiscount}
                resetPosition="right"
                onReset={() => onChange({ ...inputItem, manualDiscount: originalDiscount })}
                onChange={(value) => {
                  if (value < 0 || value > 100) {
                    toast('Discount must be between 0% and 100%', 'error');
                    return;
                  }

                  onChange({ ...inputItem, manualDiscount: value });
                }}
              />
            ) : (
              <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
                {product?.discountPercentage + ' %'}
              </Typography>
            )}
          </div>
        </div>

        {/* Quantity */}
        <div className="flex-[0_1_10%] flex flex-col">
          <div className="truncate min-h-[25px] py[12px] px-[8px] border-t-0 border-x-0 border-b border-solid border-b-[var(--grey-200)]">
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
              Qty.
            </Typography>
          </div>
          <div className={classNames('min-h-[70px] py[12px] px-[8px] flex flex-col items-center justify-center gap-2')}>
            {isEditing ? (
              <FieldCounter
                value={quantity}
                onChange={(newQty) => {
                  onChange({ ...inputItem, quantity: newQty });
                  checkAvailableQuantity(newQty);
                }}
                min={!isRental ? 0 : undefined}
                allowDecimals={!isRental}
              />
            ) : (
              <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
                {product?.quantity}
              </Typography>
            )}
          </div>
        </div>

        {/* Total */}
        <div className="flex-[0_1_8%] flex flex-col">
          <div className="truncate min-h-[25px] py[12px] px-[8px] border-t-0 border-x-0 border-b border-solid border-b-[var(--grey-200)] flex items-center justify-between">
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
              Total
            </Typography>
            {isEditing && (
              <Button
                className="button button-tertiary !p-0"
                onClick={() => onDelete(isNewProduct ? inputItem.id : `${product?.id}`)}
              >
                <Icon icon="Trash" size="medium" />
              </Button>
            )}
          </div>
          <div className="min-h-[70px] py[12px] px-[8px] flex items-center">
            <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
              {displayMoneyFormat(
                isTaxInclusive && totalTaxPercentage ? total * (1 + totalTaxPercentage / 100) : total,
                location.currency,
              )}
            </Typography>
          </div>
        </div>
      </div>

      {isRental && isShowingPrices && rentalPrices?.length && (
        <div className={'flex-[0_1_calc(100%-20px)] show-prices'}>
          <div className={'invoice-table m-[10px] min-w-full'}>
            <div className="grid grid-flow-col gap-y-2 gap-x-2">
              {rentalPrices.map((price: RentalProductPrice) => {
                const editableFieldVal =
                  customOrderRentalRates?.find(
                    (r) =>
                      r.productId === price.productId &&
                      r.durationUnit === price.durationUnit &&
                      r.durationValue === price.durationValue,
                  ) ??
                  originalCustomOrderRentalRates?.find(
                    (r) =>
                      r.productId === price.productId &&
                      r.durationUnit === price.durationUnit &&
                      r.durationValue === price.durationValue,
                  ) ??
                  price;
                return (
                  <div className="pt-1 px-4">
                    <Typography
                      type="text-sm"
                      weight="regular"
                      className="text-[var(--grey-500)] border-solid border-t-0 border-x-0 border-b border-b-[var(--grey-200)]"
                    >
                      {price.type === OrderType.Rental ? price.durationLabel : 'Price'}
                    </Typography>
                    {isCustomOrderRentalRatesEnabled && isEditing ? (
                      <>
                        <Typography type="text-sm" className="text-quipli-black-800">
                          {price.type === OrderType.Rental &&
                          price.originalPrice &&
                          price.originalPrice.price !== editableFieldVal.price ? (
                            <>
                              <s className="mr-1">
                                {displayMoneyFormat(price.originalPrice!.price / 100, location.currency)}
                              </s>
                            </>
                          ) : (
                            ''
                          )}
                        </Typography>

                        <EditableField
                          type="number"
                          value={editableFieldVal}
                          formattedValue={displayMoneyFormat(editableFieldVal.price / 100, location.currency)}
                          onChange={(value) => {
                            if (location.id) {
                              const maxEndDate = startDate.plus(order.cycleBillingTerms?.cycleLength || { days: 0 });
                              calculatePriceAsAdmin({
                                productId: inputItem.productId,
                                orderProductId: !isNaN(Number(inputItem.id)) ? parseInt(inputItem.id) : undefined,
                                orderId: order.id,
                                locationId: location.id,
                                start: startDate,
                                end: endDate
                                  ? order.isCycleBilled
                                    ? DateTime.min(endDate, maxEndDate)
                                    : endDate
                                  : maxEndDate,
                                type: OrderType.Rental,
                                taxCodeId: taxCodeId,
                                customOrderRentalRates: [
                                  ...(customOrderRentalRates || []).filter(
                                    (r) =>
                                      (!!(r as RentalProductPrice).originalPrice?.id || !!r.action) &&
                                      !(
                                        r.productId === price.productId &&
                                        r.durationUnit === price.durationUnit &&
                                        r.durationValue === price.durationValue
                                      ),
                                  ),
                                  {
                                    ...editableFieldVal,
                                    price: value * 100,
                                    action:
                                      price.originalPrice?.price && value * 100 === price.originalPrice?.price
                                        ? 'remove'
                                        : price.originalPrice?.price
                                        ? 'update'
                                        : 'add',
                                    idToChange:
                                      price.originalPrice?.price && value * 100 === price.originalPrice?.price
                                        ? price.id
                                        : price.originalPrice?.price
                                        ? price.id
                                        : undefined,
                                  },
                                ],
                              }).then((calculatedPrice) => {
                                if (isErrorCalculatePriceResult(calculatedPrice)) {
                                  toast(calculatedPrice.error, 'error');
                                  return;
                                }
                                if (calculatedPrice.taxedPrice) {
                                  setTaxedUnitPrice(calculatedPrice.taxedPrice / 100);
                                  setTotalTaxPercentage(calculatedPrice.taxPercentage);
                                }

                                onChange({
                                  ...inputItem,
                                  customPrice: isTaxInclusive ? calculatedPrice.taxedPrice : calculatedPrice.totalPrice,
                                  customOrderRentalRates: [
                                    ...(customOrderRentalRates || []).filter(
                                      (r) =>
                                        !(
                                          r.productId === price.productId &&
                                          r.durationUnit === price.durationUnit &&
                                          r.durationValue === price.durationValue
                                        ),
                                    ),
                                    {
                                      ...editableFieldVal,
                                      price: value * 100,
                                      action:
                                        price.originalPrice?.price && value * 100 === price.originalPrice?.price
                                          ? 'remove'
                                          : price.originalPrice?.price
                                          ? 'update'
                                          : 'add',
                                      idToChange:
                                        price.originalPrice?.price && value * 100 === price.originalPrice?.price
                                          ? price.id
                                          : price.originalPrice?.price
                                          ? price.id
                                          : undefined,
                                    },
                                  ],
                                } as OrderInputRental);
                              });
                            }
                          }}
                          canReset={
                            (!!price.originalPrice && editableFieldVal.price !== price.originalPrice.price) ||
                            (!price.originalPrice && editableFieldVal.price !== price.price)
                          }
                          onReset={() => {
                            if (location.id) {
                              const maxEndDate = startDate.plus(order.cycleBillingTerms?.cycleLength || { days: 0 });
                              calculatePriceAsAdmin({
                                productId: inputItem.productId,
                                orderId: order.id,
                                locationId: location.id,
                                start: startDate,
                                end: endDate
                                  ? order.isCycleBilled
                                    ? DateTime.min(endDate, maxEndDate)
                                    : endDate
                                  : maxEndDate,
                                type: OrderType.Rental,
                                customOrderRentalRates: [
                                  ...(customOrderRentalRates || []).filter(
                                    (r) =>
                                      (!!(r as RentalProductPrice).originalPrice?.id || !!r.action) &&
                                      !(
                                        r.productId === price.productId &&
                                        r.durationUnit === price.durationUnit &&
                                        r.durationValue === price.durationValue
                                      ),
                                  ),
                                  price.originalPrice?.id
                                    ? { ...price.originalPrice, action: 'remove', idToChange: price.id }
                                    : undefined,
                                ].filter((r) => !!r) as CustomOrderPriceRequest[],
                                taxCodeId: taxCodeId,
                              }).then((calculatedPrice) => {
                                if (isErrorCalculatePriceResult(calculatedPrice)) {
                                  toast(calculatedPrice.error, 'error');
                                  return;
                                }

                                if (calculatedPrice.taxedPrice) {
                                  setTaxedUnitPrice(calculatedPrice.taxedPrice / 100);
                                  setTotalTaxPercentage(calculatedPrice.taxPercentage);
                                }

                                onChange({
                                  ...inputItem,
                                  customPrice: isTaxInclusive ? calculatedPrice.taxedPrice : calculatedPrice.totalPrice,
                                  customOrderRentalRates: [
                                    ...(customOrderRentalRates || []).filter(
                                      (r) =>
                                        !(
                                          r.productId === price.productId &&
                                          r.durationUnit === price.durationUnit &&
                                          r.durationValue === price.durationValue
                                        ),
                                    ),
                                    price.originalPrice?.id
                                      ? { ...price.originalPrice, action: 'remove', idToChange: price.id }
                                      : undefined,
                                  ].filter((r) => !!r) as CustomOrderPriceRequest[],
                                } as OrderInputRental);
                              });
                            }
                          }}
                        />
                      </>
                    ) : (
                      <div className="py-2">
                        <Typography type="text-sm" className="text-quipli-black-800">
                          {price.type === OrderType.Rental && price.originalPrice ? (
                            <>
                              <s className="mr-1">
                                {displayMoneyFormat(price.originalPrice!.price / 100, location.currency)}
                              </s>
                              {displayMoneyFormat(price.price / 100, location.currency)}
                            </>
                          ) : (
                            displayMoneyFormat(price.price / 100, location.currency)
                          )}
                        </Typography>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {isEditing && deliveryDescription && (
        <div className="p-2">
          <Typography type="text-sm" weight="regular" className="text-quipli-black-700">
            Delivery: {deliveryDescription.text}
          </Typography>
        </div>
      )}
    </div>
  ) : null;
};
